import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../api/apiConfig';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface Person {
  id: number;
  name: string;
  relationship?: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string, selectedPersonIds: number[]) => void;
  placeholder?: string;
  rows?: number;
  style?: React.CSSProperties;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  placeholder,
  rows = 3,
  style = {},
}) => {
  const [people, setPeople] = useState<Person[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [selectedPersonIds, setSelectedPersonIds] = useState<number[]>([]);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchPeople = async () => {
      try {
        const response = await apiClient.get('/api/people/');
        setPeople(response.data);
      } catch (error) {
        console.error('Error fetching people:', error);
      }
    };

    fetchPeople();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue, selectedPersonIds);

    // Get cursor position
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);

    // Check if we're in a mention context
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      setMentionSearch(mentionMatch[1]);
      setShowMentions(true);
      setSelectedMentionIndex(0);
    } else {
      setShowMentions(false);
    }
  };

  const filteredPeople = people.filter(person =>
    person.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const insertMention = (person: Person) => {
    const textBeforeMention = value.slice(0, cursorPosition - mentionSearch.length - 1);
    const textAfterMention = value.slice(cursorPosition);
    const newValue = `${textBeforeMention}@${person.name} ${textAfterMention}`;
    const newSelectedPersonIds = [...selectedPersonIds, person.id];
    setSelectedPersonIds(newSelectedPersonIds);
    onChange(newValue, newSelectedPersonIds);
    setShowMentions(false);

    // Set cursor position after the inserted mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = textBeforeMention.length + person.name.length + 2; // +2 for @ and space
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev =>
          prev < filteredPeople.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : prev);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredPeople[selectedMentionIndex]) {
          insertMention(filteredPeople[selectedMentionIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    }
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
  };

  const textareaStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    border: `1px solid ${isTextareaFocused ? COLORS.accent.default : COLORS.border.default}`,
    padding: SPACING[8],
    fontSize: TYPOGRAPHY.body.sizes.default,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    color: COLORS.text.primary,
    backgroundColor: COLORS.bg.panel,
    outline: 'none',
    resize: 'vertical',
    ...style,
  };

  const mentionsListStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: 10,
    marginTop: SPACING[4],
    width: '256px',
    borderRadius: RADIUS,
    backgroundColor: COLORS.bg.panel,
    border: `1px solid ${COLORS.border.default}`,
    maxHeight: '240px',
    overflowY: 'auto',
  };

  const getMentionItemStyle = (index: number): React.CSSProperties => ({
    width: '100%',
    padding: `${SPACING[8]} ${SPACING[16]}`,
    textAlign: 'left',
    fontSize: TYPOGRAPHY.body.sizes.default,
    display: 'flex',
    alignItems: 'center',
    gap: SPACING[8],
    backgroundColor: index === selectedMentionIndex || hoveredIndex === index
      ? COLORS.bg.hover
      : 'transparent',
    border: 'none',
    cursor: 'pointer',
  });

  const iconStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    color: COLORS.text.tertiary,
  };

  const nameStyle: React.CSSProperties = {
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.primary,
  };

  const relationshipStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.body.sizes.small,
    color: COLORS.text.secondary,
  };

  return (
    <div style={containerStyle}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsTextareaFocused(true)}
        onBlur={() => setIsTextareaFocused(false)}
        placeholder={placeholder}
        rows={rows}
        style={textareaStyle}
      />

      {showMentions && filteredPeople.length > 0 && (
        <div
          ref={mentionsListRef}
          style={mentionsListStyle}
        >
          {filteredPeople.map((person, index) => (
            <button
              key={person.id}
              style={getMentionItemStyle(index)}
              onClick={() => insertMention(person)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <UserCircleIcon style={iconStyle} />
              <div>
                <div style={nameStyle}>{person.name}</div>
                {person.relationship && (
                  <div style={relationshipStyle}>{person.relationship}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
